import React, { useState, useEffect, useRef } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { LuArrowRight, LuMenu, LuX } from 'react-icons/lu';
import './Header.scss';
import logoHorizontal from '../assets/images/logos/logo_horizontal.svg';
import logoPrimary from '../assets/images/logos/logo_only_primary.svg';

function Header() {
    const [isScrolled, setIsScrolled] = useState(false);
    const [isOpen, setIsOpen] = useState(false);
    const headerRef = useRef(null);
    const toggleRef = useRef(null);
    const location = useLocation();
    const isHome = location.pathname === '/' || location.pathname === '/home';

    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 8);
        const onResize = () => {
            if (window.innerWidth >= 768) setIsOpen(false);
        };
        onScroll();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onResize);
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onResize);
        };
    }, []);

    useEffect(() => setIsOpen(false), [location]);

    useEffect(() => {
        if (!isOpen) return;
        const onKeyDown = event => {
            if (event.key === 'Escape') {
                setIsOpen(false);
                toggleRef.current?.focus();
            }
        };
        const onPointerDown = event => {
            if (!headerRef.current?.contains(event.target)) setIsOpen(false);
        };
        document.addEventListener('keydown', onKeyDown);
        document.addEventListener('pointerdown', onPointerDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
            document.removeEventListener('pointerdown', onPointerDown);
        };
    }, [isOpen]);

    return (
        <header ref={headerRef} className={`header${isScrolled ? ' scrolled' : ''}`}
                onBlur={event => {
                    // Tabbing past the last link would otherwise leave the open menu covering the focused content.
                    if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget)) setIsOpen(false);
                }}>
            <nav className="navbar navbar-expand-md" aria-label="Main navigation">
                <div className="container-fluid">
                    <Link className="navbar-brand" to="/" onClick={() => setIsOpen(false)} aria-label="Arkham Law home">
                        <picture>
                            <source media="(max-width: 489px)" srcSet={logoPrimary} />
                            <img src={logoHorizontal} alt="Arkham Law" className="navbar-logo" />
                        </picture>
                    </Link>
                    <button ref={toggleRef} className="navbar-toggler" type="button"
                            aria-controls="navbarNav" aria-expanded={isOpen}
                            aria-label={isOpen ? 'Close navigation' : 'Open navigation'}
                            onClick={() => setIsOpen(open => !open)}>
                        {isOpen ? <LuX aria-hidden="true" /> : <LuMenu aria-hidden="true" />}
                    </button>
                    <div className={`collapse navbar-collapse${isOpen ? ' show' : ''}`} id="navbarNav">
                        <ul className="navbar-nav ms-auto">
                            <li className="nav-item">
                                <Link className={`nav-link${isHome ? ' active' : ''}`} to="/"
                                      aria-current={isHome ? 'page' : undefined} onClick={() => setIsOpen(false)}>Home</Link>
                            </li>
                            <li className="nav-item"><NavLink className="nav-link" to="/about" onClick={() => setIsOpen(false)}>About</NavLink></li>
                            <li className="nav-item"><NavLink className="nav-link" to="/services" onClick={() => setIsOpen(false)}>Services</NavLink></li>
                            <li className="nav-item">
                                <NavLink className="nav-link contact-link" to="/contact" onClick={() => setIsOpen(false)}>
                                    Contact <span className="icon-wrapper"><LuArrowRight aria-hidden="true" /></span>
                                </NavLink>
                            </li>
                        </ul>
                    </div>
                </div>
            </nav>
        </header>
    );
}

export default Header;
